/**
 * Usage statistics — one record per day, added to as things happen.
 *
 * The desktop app keeps one row per day (`usage_days`) and the account area one
 * row per organization, member and day (`usage_days` in D1); both hold this
 * document. It is **additive**: everything in it is a counter, so a day is
 * brought up to date by adding an increment to it, two days are combined by
 * adding them, and a period is the sum of its days. Nothing in it is a ratio —
 * every average a page states (cost per landed task, time to land) is derived
 * from two counters kept side by side, which is what keeps the sum exact.
 *
 * Keys are short because the row is sent as it is stored:
 *
 *   b    buckets, by `model|effort|kind`
 *          usage     t turns · i input · o output · cr cache read · cw cache
 *                    write (tokens) · c cost in micro-dollars · w active ms
 *          outcomes  s started · l landed (a step: passed) · f failed ·
 *                    x stopped by the user · r re-run or re-asked
 *   k    merge conflicts met
 *   tl   time to land, summed, in ms · tn how many landings that sums
 *
 * `kind` says what the work was. A workflow step is `judge` (plan, review),
 * `build` (implementation) or `mech` (tests, checks, commits) — the three tiers
 * of a step's pair — and everything a task does outside a step is `task`. A
 * task's own outcomes are filed under `task` with the pair the task was started
 * on; a step's under the step's kind with the pair it ran on.
 */

export const STEP_KINDS = ['judge', 'build', 'mech', 'task'] as const;
export type StepKind = (typeof STEP_KINDS)[number];

export const USAGE_FIELDS = ['t', 'i', 'o', 'cr', 'cw', 'c', 'w', 's', 'l', 'f', 'x', 'r'] as const;
export type UsageField = (typeof USAGE_FIELDS)[number];
export type UsageBucket = Partial<Record<UsageField, number>>;

export interface UsageDay {
  b: Record<string, UsageBucket>;
  k?: number;
  tl?: number;
  tn?: number;
}

/** A day of usage, named by its local calendar date. */
export interface UsageDayRow {
  day: string;
  data: UsageDay;
}

export type DayIncrement = Partial<Pick<UsageDay, 'k' | 'tl' | 'tn'>>;

/** How many buckets one day may hold — a model, an effort and a kind each; far above any real day. */
export const MAX_BUCKETS = 200;
/** Per counter, per day. Above what a day can hold, below what a JSON number loses precision at. */
const MAX_COUNT = 1e15;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MODEL = /^[a-z0-9][a-z0-9.-]{0,47}$/;
const EFFORT = /^[a-z]{0,12}$/;

export function emptyDay(): UsageDay {
  return { b: {} };
}

export function isDayKey(value: unknown): value is string {
  if (typeof value !== 'string' || !DAY.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** The local calendar date of a moment, as a day key. */
export function dayOf(at: number | Date): string {
  const date = typeof at === 'number' ? new Date(at) : at;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The day key `delta` days from `day`. */
export function shiftDay(day: string, delta: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

/** Every day key from `from` to `to`, both included, oldest first. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let day = from; day <= to && out.length < 4000; day = shiftDay(day, 1)) out.push(day);
  return out;
}

export function bucketKey(model: string, effort: string, kind: StepKind): string {
  return `${cleanModel(model)}|${EFFORT.test(effort) ? effort : ''}|${kind}`;
}

export function parseBucketKey(key: string): { model: string; effort: string; kind: StepKind } | null {
  const [model, effort, kind, ...rest] = key.split('|');
  if (rest.length || model === undefined || effort === undefined) return null;
  if (!MODEL.test(model) || !EFFORT.test(effort) || !(STEP_KINDS as readonly string[]).includes(kind ?? '')) return null;
  return { model, effort, kind: kind as StepKind };
}

/** A model id as a bucket names it — lower case, a bounded alphabet, `unknown` when there is nothing left. */
export function cleanModel(model: string): string {
  const kept = model
    .toLowerCase()
    .replace(/[^a-z0-9.-]/g, '')
    .replace(/^[.-]+/, '')
    .slice(0, 48);
  return kept || 'unknown';
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.min(Math.round(value), MAX_COUNT) : 0;
}

function addBucket(into: UsageBucket, add: UsageBucket): UsageBucket {
  const out: UsageBucket = { ...into };
  for (const field of USAGE_FIELDS) {
    const sum = count((into[field] ?? 0) + (add[field] ?? 0));
    if (sum) out[field] = sum;
    else delete out[field];
  }
  return out;
}

/**
 * `day` with `add` added to one bucket and to the day's own counters. Never
 * mutates `day`. A bucket the day cannot hold any more is folded into the first
 * one of its kind rather than dropped: the totals stay right even where the
 * breakdown cannot.
 */
export function addToDay(day: UsageDay, key: string | null, add: UsageBucket, extra: DayIncrement = {}): UsageDay {
  const next: UsageDay = { ...day, b: { ...day.b } };
  if (key && USAGE_FIELDS.some((field) => count(add[field]))) {
    const target =
      key in next.b || Object.keys(next.b).length < MAX_BUCKETS
        ? key
        : (Object.keys(next.b).find((k) => k.endsWith(`|${key.split('|')[2]}`)) ?? Object.keys(next.b)[0]);
    next.b[target] = addBucket(next.b[target] ?? {}, add);
  }
  for (const field of ['k', 'tl', 'tn'] as const) {
    const sum = count((day[field] ?? 0) + (extra[field] ?? 0));
    if (sum) next[field] = sum;
    else delete next[field];
  }
  return next;
}

/** Two days, or two periods, added together. */
export function mergeDays(a: UsageDay, b: UsageDay): UsageDay {
  let out = addToDay(a, null, {}, { k: b.k, tl: b.tl, tn: b.tn });
  for (const [key, bucket] of Object.entries(b.b)) out = addToDay(out, key, bucket);
  return out;
}

/** Every day of a list, as one. */
export function sumDays(days: readonly UsageDay[]): UsageDay {
  return days.reduce(mergeDays, emptyDay());
}

/**
 * A day as somebody else wrote it — the account area reading what an install
 * sent, an install reading its own row back. What does not fit the grammar is
 * refused rather than repaired, with the reason; a counter that is not a
 * non-negative whole number is one of those.
 */
export function parseUsageDay(input: unknown): { ok: true; day: UsageDay } | { ok: false; problem: string } {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return { ok: false, problem: 'Expected an object.' };
  const raw = input as Record<string, unknown>;
  for (const key of Object.keys(raw)) {
    if (!['b', 'k', 'tl', 'tn'].includes(key)) return { ok: false, problem: `${key}: not a field of a usage day.` };
  }
  const buckets = raw.b ?? {};
  if (!buckets || typeof buckets !== 'object' || Array.isArray(buckets)) return { ok: false, problem: 'b: expected an object.' };
  const entries = Object.entries(buckets as Record<string, unknown>);
  if (entries.length > MAX_BUCKETS) return { ok: false, problem: `b: more than ${MAX_BUCKETS} buckets.` };
  const day: UsageDay = { b: {} };
  for (const [key, value] of entries) {
    if (!parseBucketKey(key)) return { ok: false, problem: `b.${key.slice(0, 80)}: not a model|effort|kind key.` };
    if (!value || typeof value !== 'object' || Array.isArray(value)) return { ok: false, problem: `b.${key}: expected an object.` };
    const bucket: UsageBucket = {};
    for (const [field, n] of Object.entries(value as Record<string, unknown>)) {
      if (!(USAGE_FIELDS as readonly string[]).includes(field)) return { ok: false, problem: `b.${key}.${field}: not a counter.` };
      if (!isCounter(n)) return { ok: false, problem: `b.${key}.${field}: expected a whole number.` };
      if (n) bucket[field as UsageField] = n;
    }
    day.b[key] = bucket;
  }
  for (const field of ['k', 'tl', 'tn'] as const) {
    const n = raw[field];
    if (n === undefined) continue;
    if (!isCounter(n)) return { ok: false, problem: `${field}: expected a whole number.` };
    if (n) day[field] = n;
  }
  return { ok: true, day };
}

function isCounter(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_COUNT;
}
